import store from 'store';
import qs from 'query-string';

import { EVENT_ADDON_ITEM_TYPE } from '../../../../../constants/event-items';
import { entityRequiresVGBContext } from '../../../../../util/album';

export const FREEMIUM_EVENT_CREATE_FORM_LOCAL_STORAGE_KEY =
  'FREEMIUM_EVENT_CREATE_FORM_LOCAL_STORAGE_KEY';

/**
 * Given a set of event addon item types, orders them based off priority.
 *
 * @param {Array} types the event addon item types
 * @returns {*}
 */
export const sortEventAddonItemTypesByPriority = types => {
  const priority = [
    EVENT_ADDON_ITEM_TYPE.WALDOFIED,
    EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
    EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
  ];

  return types.sort((a, b) => {
    const indexA = priority.indexOf(a);
    const indexB = priority.indexOf(b);

    if (indexA === -1 && indexB !== -1) {
      return 1;
    }

    if (indexA !== -1 && indexB === -1) {
      return -1;
    }

    return indexA - indexB;
  });
};

export const sortEventAddonItemsByPriority = eventAddonItems => {
  const priority = [
    EVENT_ADDON_ITEM_TYPE.WALDOFIED,
    EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
    EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
  ];

  return [...eventAddonItems].sort((a, b) => {
    const typeA = a.types[0] ?? a.type;
    const typeB = b.types[0] ?? b.type;
    const indexA = priority.indexOf(typeA);
    const indexB = priority.indexOf(typeB);

    if (indexA === -1 && indexB !== -1) {
      return 1;
    }

    if (indexA !== -1 && indexB === -1) {
      return -1;
    }

    return indexA - indexB;
  });
};

/**
 * Retrieves the appropriate meta key for event addon items based on type(s)
 *
 * @param {Array} types the event addon item types
 * @param {String} albumSubType the album sub type
 *
 * @returns {string|*}
 */
export const getEventAddonItemTypeMetaKey = (types, albumSubType) => {
  if (types.length === 1) {
    return entityRequiresVGBContext({ album_subtype: albumSubType }) &&
      types[0] === EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO
      ? 'VIDEO_GUESTBOOK'
      : types[0];
  }

  const sortedTypes = sortEventAddonItemTypesByPriority(types);

  return sortedTypes
    .map(type =>
      type === EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO &&
      entityRequiresVGBContext({ album_subtype: albumSubType })
        ? 'VIDEO_GUESTBOOK'
        : type,
    )
    .join(',');
};

/**
 * Determines if an event addon item is included as part of the selection
 *
 * @param {Array} eventAddonItems The existent Event Addon Items
 * @param {Array} selectedEventAddonItemIds The selected Event Addon Item IDs
 * @param {String} eventAddonItemType The type to check for
 *
 * @returns {boolean}
 */
export const includesEventItemByType = (
  eventAddonItems,
  selectedEventAddonItemIds,
  eventAddonItemType,
) => {
  if (eventAddonItems?.length < 1 || selectedEventAddonItemIds?.length < 1) {
    return false;
  }

  const eventAddonItem = eventAddonItems.find(
    ({ types }) => types[0] === eventAddonItemType,
  );

  if (!eventAddonItem) {
    return false;
  }

  return selectedEventAddonItemIds.includes(eventAddonItem.id);
};

/**
 * Determines if the Waldofied Item is included as part of the selection
 *
 * @param {Array} eventAddonItems The existent Event Addon Items
 * @param {Array} selectedEventAddonItemIds The selected Event Addon Item IDs
 *
 * @returns {boolean}
 */
export const includesWaldofiedAddonItem = (
  eventAddonItems,
  selectedEventAddonItemIds,
) => {
  return includesEventItemByType(
    eventAddonItems,
    selectedEventAddonItemIds,
    EVENT_ADDON_ITEM_TYPE.WALDOFIED,
  );
};

/**
 * Determines if an event addon item the only item on a selection
 *
 * @param {Array} eventAddonItems The existent Event Addon Items
 * @param {Array} selectedEventAddonItemIds The selected Event Addon Item IDs
 * @param {String} eventAddonItemType The type to check for
 *
 * @returns {boolean}
 */
export const isSingleEventItemConfigByType = (
  eventAddonItems = [],
  selectedEventAddonItemIds = [],
  eventAddonItemType,
) => {
  return (
    selectedEventAddonItemIds?.length === 1 &&
    includesEventItemByType(
      eventAddonItems,
      selectedEventAddonItemIds,
      eventAddonItemType,
    )
  );
};

/**
 * Returns an ordered array of steps based off of the variant provided.
 *
 * @param {Array<String>>} variant Array containing the base order of steps.
 * @param {Object} stepsObject Parsed steps object containing all available steps.
 * @returns {Array<Object>} An array ordered steps the user must go through in the session.
 */
export const getStepArrayFromVariant = (variant, stepsObject) => {
  return variant
    .map(key => stepsObject[key])
    .filter(step => step && !step.skip);
};

export const getFormDataFromLocalStore = () => {
  const data = store.get(FREEMIUM_EVENT_CREATE_FORM_LOCAL_STORAGE_KEY) || {};

  const valuesDateKeys = [
    'date',
    'start_date',
    'end_date',
    'date_of_birth',
    'date_of_passing',
    'graduation_date',
  ];

  const parsedValues = Object.keys(data?.values ?? {}).reduce((values, key) => {
    if (valuesDateKeys.includes(key) && data?.values[key]) {
      return {
        ...values,
        [key]: new Date(data?.values[key]),
      };
    }

    return values;
  }, data?.values);

  return {
    ...data,
    values: parsedValues,
  };
};

const shouldUseLocalStorage = ({ location }) => {
  const localStorage = store.get(FREEMIUM_EVENT_CREATE_FORM_LOCAL_STORAGE_KEY);
  const locationSearch = qs.parse(location.search, { arrayFormat: 'bracket' });
  const hasAuth = !!locationSearch?.authToken;
  const eventTypeMatches =
    localStorage?.eventType === locationSearch?.eventType ||
    (!locationSearch?.eventType && !localStorage?.eventType);

  return localStorage && hasAuth && eventTypeMatches;
};

/**
 * Based off the localStorage file + the location search query, returns the user with an
 * initial state for their data.
 *
 * @param {Object} opts
 * @param {Object} opts.location React Router location object
 * @returns {Object} An object containing initial state.
 */
export const getInitialStateData = ({ location }) => {
  const locationSearch = qs.parse(location.search, { arrayFormat: 'bracket' });

  if (shouldUseLocalStorage({ location })) {
    return getFormDataFromLocalStore();
  }

  return locationSearch;
};

/**
 * Stores a new state for the localStorage store.
 *
 * @param {Object} opts
 * @param {Object} opts.values
 * @param {Object} opts.stepProps
 * @param {Array<String>} opts.currentSelectedEventAddonItemIds
 * @param {Object} opts.initialState
 * @param {Object} opts.location
 * @param {Number} opts.activeStepKey
 */
export const onFormDataLocalStore = ({
  values,
  stepProps,
  currentSelectedEventAddonItemIds,
  initialState,
  location,
  activeStepKey,
}) => {
  // Avoid storing methods in the stepProps object, as well as references and other real-time computated items
  // as to not dirty initial stepProps with old data.
  const ignoredStepPropKeys = ['user', 'submitButtonRef', 'authenticated'];

  const storedStepProps = Object.keys(stepProps).reduce((data, key) => {
    if (
      typeof stepProps[key] === 'function' ||
      ignoredStepPropKeys.includes(key)
    ) {
      return data;
    }

    return {
      ...data,
      [key]: stepProps[key],
    };
  }, {});

  store.set(FREEMIUM_EVENT_CREATE_FORM_LOCAL_STORAGE_KEY, {
    ...(initialState || {}),
    stepProps: storedStepProps,
    values,
    selectedEventAddonItemIds: currentSelectedEventAddonItemIds,
    search: location.search,
    activeStepKey,
  });
};

export const onLocalStoreClear = () => {
  store.set(FREEMIUM_EVENT_CREATE_FORM_LOCAL_STORAGE_KEY, undefined);
};
